import { BlockUI } from 'primereact/blockui';
import { Checkbox } from 'primereact/checkbox';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Dropdown } from 'primereact/dropdown';
import { FileUpload } from 'primereact/fileupload';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Button } from 'primereact/button';
import React, { useRef, useState } from "react";
import { useQuery } from 'react-query';
import PageHeading from "../../../components/PageHeading";
import authApi from "../../../service/IdApi";
import LazyState from '../../../utils/LazyState';
import PageLoadError from '../../../components/PageLoadError';

const ImportCompanyData = () => {
    const [stateSelection, setStateSelection] = useState('FL');
    const [isImporting, setIsImporting] = useState(false);
    const [importMessage, setImportMessage] = useState(null);
    const [importMessageType, setImportMessageType] = useState('success');
    const [companyData, setCompanyData] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const fileUploadRef = useRef(null);
    const [useKeywordSearch, setUseKeywordSearch] = useState(true);
    const [searchText, setSearchText] = useState('');
    const [lazyState, setLazyState] = useState(() => {
        return LazyState.getImportCompanyDataLazyState();
    });
    const [timer, setTimer] = useState(null);

    const { isLoading, isError } = useQuery(["ImportCompanyData", lazyState], async () => {
        let body = {
            first: lazyState.first,
            rows: lazyState.rows,
            sortField: lazyState.sortField,
            sortOrder: lazyState.sortOrder,
            searchText: searchText,
            useKeywordSearch: useKeywordSearch,
        }

        const response = await authApi.post("admin/importedCompanies", body);
        setCompanyData(response.data?.companies || []);
        setTotalRecords(response.data?.totalRecords || 0);
        return response;
    });

    const handleImport = async (event) => {
        if (!event.files || !stateSelection) return;
        setIsImporting(true);
        setImportMessage(null);

        const formData = new FormData();
        formData.append("state", stateSelection);
        formData.append("file", event.files[0]);

        try {
            // Replace this URL with the actual API endpoint
            await authApi.post("admin/import-company-data", formData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            });

            setImportMessage("File imported successfully!");
            setImportMessageType("success");

            //This will cause a refresh of the datatable
            setLazyState(LazyState.resetLazyState(lazyState));
        } catch (error) {
            setImportMessage("Import failed: " + (error?.response?.data?.msg || error.message));
            setImportMessageType("danger");
        } finally {
            setIsImporting(false);
            fileUploadRef.current.clear();
        }
    };


    const updateUseKeywordSearch = (event) => {
        setUseKeywordSearch(event.checked);
        setLazyState(LazyState.resetLazyState(lazyState));
    };

    const updateSearchText = (event) => {
        if (searchText !== event.target.value) {
            setSearchText(event.target.value);
            if (timer) {
                clearTimeout(timer);
            }

            const newTimer = setTimeout(() => {
                setLazyState(LazyState.resetLazyState(lazyState));
            }, 500);

            setTimer(newTimer);
        }
    };

    const clearSearchText = () => {
        if (timer) {
            clearTimeout(timer);
            setTimer(null);
        }

        setSearchText('');
        setLazyState(LazyState.resetLazyState(lazyState));
    };

    const onPage = (event) => {
        setLazyState(event);
    };

    const onSort = (event) => {
        setLazyState(event);
    };

    const pageHeader = (
        <PageHeading pageTitle="Admin - Import Company Data">
        </PageHeading>
    );


    const filedOnDateBodyTemplate = (rowData) => {
        if (!rowData.filedOnDate) return '';
        const date = new Date(rowData.filedOnDate);
        return date.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
    };

    const addressBodyTemplate = (rowData) => {
        return (
            <>
                <span className="white-space-nowrap">
                    {rowData.address1}
                    {rowData.address2 && <br />}
                    {rowData.address2}
                </span>
                <span>
                    {rowData.city && <br />}
                    {rowData.city}
                    {rowData.city && <span>, </span>}
                    {rowData.state} {rowData.zip}
                </span>
            </>

        );
        return rowData.address1;
    };

    return (
        <div>
            {pageHeader}
            <BlockUI blocked={isImporting} template={
                <div className="flex align-items-center justify-content-center" style={{ width: '100%', height: '100%' }}>
                    <ProgressSpinner />
                </div>
            }>
                <div id="ImportCompanyDataForm" className="card mb-3">
                    <div className="formgrid grid p-fluid">
                        <div className="field ">
                            <label htmlFor="stateSelect" className="font-bold block mb-2 ml-1">
                                State
                            </label>
                            <Dropdown
                                id="stateSelect"
                                value={stateSelection || null}
                                onChange={e => setStateSelection(e.value)}
                                options={[
                                    { label: "Florida", value: "FL" }
                                ]}
                                className="w-full"
                                placeholder="Select State"
                            />
                        </div>
                        <div className="field ml-3">
                            <label htmlFor="fileUpload" className="font-bold block mb-2">
                                &nbsp;
                            </label>
                            <FileUpload
                                ref={fileUploadRef}
                                id="fileUpload"
                                name="file"
                                accept=".txt"
                                mode="basic"
                                uploadHandler={handleImport}
                                customUpload
                                className="w-full"
                                chooseLabel="Import File"
                                auto={true}
                                maxFileSize={5000000}

                            />
                        </div>

                        {importMessage && (
                            <div className={`col-12 mt-3 alert ${importMessageType === 'success' ? 'alert-success' : 'alert-danger'}`}>
                                {importMessage}
                            </div>
                        )}
                    </div>
                </div>
                <div className="card mb-3">

                    {isError && (
                        <PageLoadError />
                    )}
                    {!isError && (
                        <>
                            <div className="flex flex-row flex-wrap align-items-center mb-3">
                                <div className="field mb-3">
                                    <span className="">
                                        <label htmlFor="searchText" className="mr-2">Company Name</label>
                                        <input
                                            id="searchText"
                                            type="text"
                                            className="p-inputtext p-component"
                                            placeholder="Search..."
                                            value={searchText || ''}
                                            onChange={updateSearchText}
                                        />
                                    </span>
                                </div>

                                {searchText && (
                                    <div className="field mb-3">
                                        <Button
                                            type="button"
                                            className="p-button-text"
                                            icon="pi pi-times"
                                            tooltip="Clear Filter"
                                            onClick={clearSearchText}
                                        />
                                    </div>
                                )}

                                <div className="field-checkbox ml-3">
                                    <Checkbox
                                        inputId="useKeywordSearch"
                                        checked={useKeywordSearch || false}
                                        onChange={updateUseKeywordSearch}
                                    />
                                    <label for="useKeywordSearch" className="ml-2">Filter on Keywords</label>
                                </div>
                            </div>



                            <DataTable
                                value={companyData}
                                paginator
                                rows={lazyState.rows}
                                rowsPerPageOptions={[5, 25, 50, 100, 500]}
                                className="p-datatable-sm"
                                loading={isLoading}
                                totalRecords={totalRecords}
                                onPage={onPage}
                                onSort={onSort}
                                sortField={lazyState.sortField}
                                sortOrder={lazyState.sortOrder}
                                lazy
                                first={lazyState.first}
                            >
                                <Column field="companyMetaData.registeredState" sortable header="Registered State" />
                                <Column field="companyName" sortable header="Company Name" />
                                <Column field="address1" header="Address" body={addressBodyTemplate} />
                                <Column field="registeredAgentName" sortable header="Registered Agent Name" />
                                <Column
                                    field="filedOnDate"
                                    sortable
                                    header="Filed On Date"
                                    body={filedOnDateBodyTemplate}
                                />
                            </DataTable>
                        </>
                    )}
                </div>

            </BlockUI>
        </div>
    );
};

export default ImportCompanyData;
